import { useEffect, useMemo, useRef } from 'react';
import { useMountingInventoryAPI } from '@elfsight/widget-mounting-inventory';

export interface DelegateEventListening {
  addListener: (
    selector: string,
    eventType: string,
    callback: ListenerCallback
  ) => RemoveListenerFn;
}

export type ListenerCallback = (event: Event) => void;

export type RemoveListenerFn = () => void;

export function useDelegateEventListening() {
  const mountingInventoryAPI = useMountingInventoryAPI();

  const listenersRef = useRef(new Set<Listener>());

  useEffect(() => {
    if (!mountingInventoryAPI) {
      return undefined;
    }

    const removeRootAddedCallback = mountingInventoryAPI.onRootAdded((root) => {
      listenersRef.current.forEach(({ eventType, callback }) => {
        root.addEventListener(eventType, callback);
      });
    });

    const removeRootRemovedCallback = mountingInventoryAPI.onRootRemoved(
      (root) => {
        listenersRef.current.forEach(({ eventType, callback }) => {
          root.removeEventListener(eventType, callback);
        });
      }
    );

    return () => {
      removeRootAddedCallback();
      removeRootRemovedCallback();
    };
  }, [mountingInventoryAPI]);

  const api: DelegateEventListening = useMemo(() => {
    function addListener(
      selector: string,
      eventType: string,
      callback: ListenerCallback
    ) {
      const delegateCallback = createDelegateCallback(selector, callback);

      const listener: Listener = {
        selector,
        eventType,
        callback: delegateCallback
      };
      listenersRef.current.add(listener);

      mountingInventoryAPI?.getRoots().forEach((root) => {
        root.addEventListener(eventType, delegateCallback);
      });

      return function removeListener() {
        listenersRef.current.delete(listener);

        mountingInventoryAPI?.getRoots().forEach((root) => {
          root.removeEventListener(eventType, delegateCallback);
        });
      };
    }

    return { addListener };
  }, [mountingInventoryAPI]);

  return api;
}

interface Listener {
  selector: string;
  eventType: string;
  callback: ListenerCallback;
}

function createDelegateCallback(selector: string, callback: ListenerCallback) {
  return function delegateCallback(event: Event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const actualTarget = event.target.closest(selector);
    if (actualTarget) {
      callback({ ...event, target: actualTarget });
    }
  };
}
